import { useState, useEffect, useMemo, useContext } from "react";
import { isEqual } from "lodash";
import { styled, keyframes, vh } from "~/ui/style/stitches.config";
import { useStorage } from "~/state/liveblocks.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { shallow } from "@liveblocks/client";
import { trackEvent } from "~/api/analyticsApi";
import { getRoomSummary, getLatestRoomSummary } from "~/api/roomApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { Deck, DeckCard, SpreadCard } from "~/types/room/types";
import { imageUrl } from "~/utils/imageurl";
import { Vec2d } from "~/utils/useMousePosition";
import { withPadding } from "~/ui/room/Spread";
import { CopyButton } from "~/ui/components/CopyButton";
import { CardLoadingSvg } from "~/ui/room/CardFallbacks";
import { TarotReading } from "~/types/summaries";
import { Button } from "../components/Button";
import { AuthContext } from '~/utils/useAuth';
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { updateJournalEntry } from "~/api/journalApi";


const roundPosition = (pos: Vec2d): Vec2d => {
  return [Math.round(pos[0]), Math.round(pos[1])];
};

// Cards and notes use different origins; convert the card into the note frame before comparing distances.
const cardCenterInNoteFrame = (
  cardPos: Vec2d,
  spreadSize: Vec2d,
  cardSize: Vec2d
): Vec2d => {
  const padded = withPadding(spreadSize);
  const offsetX = (padded[0] * cardSize[0]) / 2;
  const offsetY = (padded[1] * cardSize[1]) / 2;
  return [
    cardPos[0] - offsetX + cardSize[0] / 2,
    cardPos[1] - offsetY + cardSize[1] / 2,
  ];
};

// Nearest note id to a card (or null); computed here, not by the LLM, so the mapping can't be mis-guessed.
const nearestNoteId = (
  cardPos: Vec2d,
  spreadSize: Vec2d,
  cardSize: Vec2d,
  notes: { id: number; content: string; position: Vec2d }[]
): number | null => {
  if (notes.length === 0) return null;
  const center = cardCenterInNoteFrame(cardPos, spreadSize, cardSize);
  const threshold = cardSize[1] * 1.5;
  let best: { id: number; dist: number } | null = null;
  for (const note of notes) {
    if (!note.content || note.content.trim() === "") continue; // skip empty/unused notes
    const dx = center[0] - note.position[0];
    const dy = center[1] - note.position[1];
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (best === null || dist < best.dist) {
      best = { id: note.id, dist };
    }
  }
  return best && best.dist <= threshold ? best.id : null;
};

const fadeIn = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

const fadeOut = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 }
});

const rotate = keyframes({
  "0%": {
    transform: "scale(0.85) rotate(0deg) ",
  },
  "100%": {
    transform: "scale(0.85) rotate(360deg) ",
  },
});


const StyledRoundSummaryButton = styled("div", {
  position: "absolute",
  bottom: "60px",
  right: "13px",
  zIndex: 4,
  "@mobile": {
    bottom: "8px",
    right: "8px",
    zIndex: 10000,
  },
  animation: `${fadeIn} 0.5s ease-in-out`,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(0.9)",
    "& .summary-button": {
      backgroundColor: "$gray900 !important",
    },
  },
  cursor: "pointer",
  "& img": {
    width: "20px",
    height: "20px",
    position: "absolute",
    top: "calc(50% - 2px)",
    left: "calc(50% + 2px)",
    transform: "translate(-50%, -50%)",
  },
  "& .inner-button-container": {
    position: "relative",
    width: "88px",
    height: "88px",
    "& .svg-container": {
      fontWeight: "300",
      animation: `${rotate} 20s linear infinite`,
      transformOrigin: "center",
      fontSize: "14px",
      letterSpacing: "0.05em",
      "@mobile": {
        fontWeight: 400,
      },
    },
    "& .summary-button": {
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      backgroundColor: "black",
      position: "absolute",
      border: "0.5px solid #5e5e5e",
      top: 0,
      left: 0,
      zIndex: -1,
      transition: "all 0.2s ease-in-out",
    },
  },

  variants: {
    Theme: {
      light: {
        "& text": {
          fill: "white",
        },
        "& img": {
          filter: "invert(1)",
        },
      },
      dark: {
        "& text": {
          fill: "black",
        },
        "&:hover": {
          "& .summary-button": {
            backgroundColor: "#f1f1f1 !important",
          },
        },
        "& .summary-button": {
          backgroundColor: "white !important",
          border: ".5px solid #c1c1c1",
        },
      },
    },
  },
});

const RoundSummaryButton = ({ onClick }: { onClick: Function }) => {
  const diameter = 96;
  const { inDarkMode } = useSnapshot(localState);
  return (
    <StyledRoundSummaryButton
      id="summary-button"
      Theme={inDarkMode ? "dark" : "light"}
      onClick={() => {
        trackEvent("/room/summary");
        onClick();
      }}
      style={isIOSNativeApp() ? {
        bottom: "36px",
        right: "16px",
        zIndex: 10000
      } : {}}
    >
      <div className="inner-button-container">
        <div className="svg-container">
          <svg viewBox={`0 0 ${diameter} ${diameter}`}>
            <path
              id="curve"
              fill="transparent"
              d="
                M 7,52
                a 42,42 0 1,1 84,0
                a 42,42 0 1,1 -84,0
              "
            />
            <text className="text" width={diameter} fill="black">
              <textPath xlinkHref="#curve">summary</textPath>
            </text>
          </svg>
        </div>
        <img src="/images/more-menu/blog.svg" />
        <div className="summary-button" id="summary-button"></div>
      </div>
    </StyledRoundSummaryButton>
  );
};

const StyledSummaryOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: vh(100),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 999999,

  '& .summary-container': {
    backgroundColor: 'inherit',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '460px',
    width: '90%',
    maxHeight: vh(80),
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: 'rgba(127, 127, 127, .5)',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      marginTop: '24px',
      marginBottom: '24px',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
  },


  '& .summary-content': {
    padding: '28px 16px',
    paddingBottom: '180px',
    position: 'relative',
    width: '100%',
    minHeight: 'min-content',
  },

  '@mobile': {
    backgroundColor: 'transparent !important',
    '& .summary-container': {
      border: 'none !important',
      borderRadius: 0,
      width: '100%',
      height: '100%',
      maxHeight: '100%!important',
      minHeight: `calc(${vh(100)} - 140px)`,
      margin: 0,
      paddingTop: '48px!important',
    },
    '& .summary-content': {
      paddingBottom: '220px!important',
    },
  },

  variants: {
    loading: {
      true: {
        '& .loading-state': {
          display: 'flex',
        },
        '& .summary-content': {
          display: 'none',
        },
      },
      false: {
        '& .loading-state': {
          display: 'none',
        },
        '& .summary-content': {
          display: 'flex',
        },
      },
    },
    Theme: {
      light: {
        backgroundColor: 'rgba(180, 180, 180, 0.4)',
        '& .summary-container': {
          backgroundColor: '$wash',
          border: "1px solid $gray600"
        },
      },
      dark: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        '& .summary-container': {
          backgroundColor: '$darkwash',
          color: '#fff',
          border: "1px solid $gray600"
        },
        '& .x-button': {
          filter: 'invert(1)',
        }
      },
    },
  },
});

const StyledAnalysisButton = styled('button', {
  fontSize: '12px',
  padding: '10px 16px',
  color: "white",
  border: "none",
  borderRadius: '11px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#333'
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.8,
    '&:hover': {
      backgroundColor: 'black'
    }
  },

  variants: {
    Theme: {
      light: {
        backgroundColor: 'white',
        border: "1px solid #000",
        color: 'black',
        '&:hover': {
          backgroundColor: '$gray200'
        },
        '&:disabled': {
          '&:hover': {
            backgroundColor: 'white'
          }
        }
      },
      dark: {
        backgroundColor: 'black',
        border: "1px solid #fff",
        color: 'white',
        '&:hover': {
          backgroundColor: '#333'
        },
        '&:disabled': {
          '&:hover': {
            backgroundColor: 'black'
          }
        }
      }
    }
  }
});

const ContinueButton = styled(StyledAnalysisButton, {
  '&:hover': {
    backgroundColor: '$gray700',
  },

  variants: {
    Theme: {
      light: {
        backgroundColor: '$grayA100',
        '&:hover': {
          backgroundColor: '$gray200'
        }
      },
      dark: {
        backgroundColor: '$gray800',
        '&:hover': {
          backgroundColor: '$gray700'
        }
      }
    }
  }
});

const StyledAnalysis = styled('div', {
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  paddingTop: '10px',

  '& img': {
    width: "30%",
    borderRadius: "8px",
    marginBottom: "10px",
    maxWidth: "100px"
  },
  '& h1': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginBottom: '3px',
    marginTop: "10px",
    fontWeight: 100
  },
  '& h2': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginTop: '2.2rem',
    marginBottom: '0.5rem',
    fontWeight: 100
  },
  '& p': {
    fontSize: "13px",
    lineHeight: '1.4',
    margin: '4px auto 4px',
  },
  '& .interpretation': {
    marginBottom: "30px",
    marginTop: "30px"
  },
  '& .overall': {
    backgroundColor: 'rgba(129, 129, 129, 0.1)',
    border: '1px solid $gray500',
    padding: '1.2rem',
    borderRadius: '12px'
  },
  '& .query': {
    marginBottom: '34px',
    background: '#e9e9e9',
    padding: '6px 10px',
    borderRadius: '20px',
    textAlign: 'center',
    '& p': {
      fontSize: '11px',
      opacity: 0.6,
    }
  },

  '& .question': {
    fontWeight: "500",
  },
  '& .card-section': {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    alignItems: 'flex-start',
  },
  '& .card-content': {
    flex: 1,
  },

  variants: {
    Theme: {
      dark: {
        '& .overall': {
          backgroundColor: '$gray800'
        },
        '& .query': {
          background: '#333',
          '& p': {
            color: '#fff'
          }
        }
      },
      light: {}
    }
  }
});

const StyledSummaryFooter = styled('div', {
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  width: 'calc(100% + 64px)',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: '1px solid',
  padding: '12px',
  marginTop: 'auto',

  variants: {
    Theme: {
      light: {
        backgroundColor: '$wash',
        borderColor: '$gray400',
      },
      dark: {
        backgroundColor: '$darkwash',
        borderColor: '$gray600',
      }
    }
  }
});

export const SummaryButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [summary, setSummary] = useState<TarotReading | null>(null);
  const [summaryDate, setSummaryDate] = useState<Date | null>(null);
  const cards = useStorage((root) => root.cards, shallow);
  const hasFlippedCards = cards.some(card => card.open);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!hasFlippedCards) {
    return null;
  }

  return (
    <>
      <RoundSummaryButton onClick={handleOpen} />
      {isOpen && (
        <SummaryOverlay
          onClose={handleClose}
          summary={summary}
          setSummary={setSummary}
          summaryDate={summaryDate}
          setSummaryDate={setSummaryDate}
        />
      )}
    </>
  );
};

interface SummaryOverlayProps {
  onClose: () => void;
  summary: TarotReading | null;
  setSummary: (summary: TarotReading | null) => void;
  summaryDate: Date | null;
  setSummaryDate: (date: Date | null) => void;
}

const hasDataChanged = (oldData: any, newData: any): boolean => {
  // Using lodash's isEqual for deep comparison that ignores property order
  return !isEqual(oldData, newData);
};

const getTableDataOnly = (data: any) => {
  const { summaryId, ...tableData } = data;
  // Strip derived LLM hints (payload-only) so they don't trigger a false "data changed".
  return {
    ...tableData,
    cards: Array.isArray(tableData.cards)
      ? tableData.cards.map(({ nearestNoteId, ...card }: any) => card)
      : tableData.cards,
    texts: Array.isArray(tableData.texts)
      ? tableData.texts.map(({ id, ...text }: any) => text)
      : tableData.texts,
  };
};

export const SummaryOverlay = ({ onClose, summary, setSummary, summaryDate, setSummaryDate }: SummaryOverlayProps) => {
  const { inDarkMode } = useSnapshot(localState);
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasGeneratedSummary, setHasGeneratedSummary] = useState(false);
  const [isFetchingSummary, setIsFetchingSummary] = useState(true);
  const [dataChanged, setDataChanged] = useState(false);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const [reflections, setReflections] = useState('');
  const [isSavingReflections, setIsSavingReflections] = useState(false);
  const [originalReflections, setOriginalReflections] = useState('');
  const [showReflectionsInput, setShowReflectionsInput] = useState(false);
  const [summaryId, setSummaryId] = useState<string | null>(null);
  const [originalSummaryData, setOriginalSummaryData] = useState<any>(null);
  const [summaryUserId, setSummaryUserId] = useState<string | null>(null);
  const [showManualReflections, setShowManualReflections] = useState(false);
  const [manualReflections, setManualReflections] = useState('');
  const [isSavingManualReflections, setIsSavingManualReflections] = useState(false);

  const cards = useStorage((root) => root.cards, shallow)
    .filter((card) => card.open)
    .slice(0, 10);
  const spread = useStorage((root) => root.spread, shallow);
  const deck = useStorage((root) => root.deck) as Deck;
  const texts = useStorage((root) => root.texts, shallow);
  const reversesOn = useStorage((root) => root.reversesOn);
  const { isOnMobile } = useSnapshot(localState);

  const cardsByFilePath = new Map(cards.map(card => [card.filePath, card]));

  // Memoize summaryData to prevent unnecessary recalculations
  const summaryData = useMemo(() => {
    const noteList = Array.from(texts).map((text, i) => ({
      id: i + 1,
      content: text.text,
      position: roundPosition(text.position),
    }));
    return {
      cards: [
        ...spread.slots.flatMap(slot => {
          const slotCards = (slot.cards || [])
            .filter(filePath => cardsByFilePath.has(filePath))
            .map(filePath => {
              const card = cardsByFilePath.get(filePath)!;

              const cardData = deck.cards.find((deckCard: DeckCard) =>
                card.filePath === deckCard.artwork ||
                card.filePath === `${deck.basePath}/${deckCard.artwork}`
              );

              const keywords = cardData?.keywords
                ? (reversesOn && card.reversed && cardData.keywordsReverse?.length > 0 && cardData.keywordsReverse[0].length > 0)
                  ? cardData.keywordsReverse
                  : cardData.keywords
                : [];

              return {
                cardName: cardData?.name || 'Unknown Card',
                reversed: reversesOn && card.reversed,
                keywords,
                imagePath: imageUrl(`/images${card.filePath}`),
                slot: {
                  name: slot.name,
                  description: slot.description
                }
              };
            });
          return slotCards;
        }),

        ...cards
          .filter(card => !spread.slots.some(slot =>
            slot.cards && slot.cards.includes(card.filePath)
          ))
          .map(card => {
            const cardData = deck.cards.find((deckCard: DeckCard) =>
              card.filePath === deckCard.artwork ||
              card.filePath === `${deck.basePath}/${deckCard.artwork}`
            );

            const keywords = cardData?.keywords
              ? (reversesOn && card.reversed && cardData.keywordsReverse?.length > 0 && cardData.keywordsReverse[0].length > 0)
                ? cardData.keywordsReverse
                : cardData.keywords
              : [];

            return {
              cardName: cardData?.name || 'Unknown Card',
              reversed: reversesOn && card.reversed,
              keywords,
              imagePath: imageUrl(`/images${card.filePath}`),
              worldPosition: roundPosition(card.position),
              nearestNoteId: nearestNoteId(
                card.position,
                spread.size,
                deck.cardSize,
                noteList
              ),
              slot: 'blank'
            };
          })
      ],
      texts: noteList
    };
  }, [cards, spread.slots, spread.size, deck.cards, deck.basePath, deck.cardSize, reversesOn, texts]);

  // Add effect to handle mobile view
  useEffect(() => {
    if (isOnMobile) {
      // Hide header
      const header = document.querySelector('.moonlight-header') as HTMLElement;
      if (header) {
        header.style.display = 'none';
      }

      // Lock body but allow overlay scroll - skip this for iOS native
      if (!isIOSNativeApp()) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }

      return () => {
        if (header) {
          header.style.display = 'flex';
        }
      };
    }
  }, [isOnMobile]);

  // Fetch the most recent summary when component mounts
  useEffect(() => {
    const fetchLatestSummary = async () => {
      try {
        setIsFetchingSummary(true);
        const roomName = window.location.pathname.split('/')[2];
        const result = await getLatestRoomSummary(roomName);

        if (result && result.summary) {
          setSummary(result.summary);
          setSummaryDate(new Date(result.createdAt));
          setHasGeneratedSummary(true);
          setSummaryId(result.id);
          setSummaryUserId(result.userId); // Store the userId
          const { summaryId: _, ...dataWithoutId } = result.originalData;
          setOriginalSummaryData(dataWithoutId);
        } else {
          setHasGeneratedSummary(false);
        }
      } catch (error) {
        console.error('Error fetching latest summary:', error);
        setHasGeneratedSummary(false);
      } finally {
        setIsFetchingSummary(false);
      }
    };

    fetchLatestSummary();
  }, [setSummary]); // Remove summaryData dependency

  // Check if data has changed since last summary
  useEffect(() => {
    if (hasGeneratedSummary && originalSummaryData && !isFetchingSummary) {
      const currentTableData = getTableDataOnly(summaryData);
      const originalTableData = getTableDataOnly(originalSummaryData);
      const hasChanged = !isEqual(originalTableData, currentTableData);
      setDataChanged(hasChanged);
    }
  }, [summaryData, hasGeneratedSummary, originalSummaryData, isFetchingSummary]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();  // Prevent events from reaching elements behind
    if (e.target === e.currentTarget) {  // Only close if clicking the actual overlay background
      onClose();
    }
  };

  const handleGetSummary = async () => {
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }

    setShowConfirm(false);
    setIsLoading(true);
    try {
      const roomName = window.location.pathname.split('/')[2];
      const response = await getRoomSummary(
        roomName,
        summaryData,
        undefined,
        isManualOnly ? (summaryId || undefined) : undefined
      );

      // Preserve existing reflections when generating summaries
      const updatedSummary = {
        ...response.summary,
        reflections: summary?.reflections || response.summary.reflections
      };

      setSummary(updatedSummary);
      setSummaryDate(response.createdAt ? new Date(response.createdAt) : new Date());
      setHasGeneratedSummary(true);
      setDataChanged(false);
      setSummaryId(response.id || summaryId);
      setSummaryUserId(user?.uuid || null); // Store the userId
      setOriginalSummaryData(summaryData);

      // Scroll to the date divider after a short delay
      setTimeout(() => {
        const dateDivider = document.querySelector('.summary-date-divider');
        if (dateDivider) {
          (dateDivider as HTMLElement).style.scrollMarginTop = '60px';
          dateDivider.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 200);
    } catch (error) {
      console.error('Failed to get summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add effect to show toast when dataChanged becomes true
  useEffect(() => {
    if (dataChanged) {
      setShowUpdateToast(true);
      const timer = setTimeout(() => {
        setShowUpdateToast(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [dataChanged]);

  // Initialize reflections when summary changes
  useEffect(() => {
    if (summary?.reflections) {
      setReflections(summary.reflections);
      setOriginalReflections(summary.reflections);
    } else {
      setReflections('');
      setOriginalReflections('');
    }
  }, [summary]);


  const getTextareaStyle = (minHeight: string) => ({
    width: '100%',
    minHeight,
    backgroundColor: inDarkMode ? '#000' : '#fff',
    border: `1px solid ${inDarkMode ? 'var(--colors-gray600)' : 'var(--colors-gray500)'}`,
    borderRadius: '12px',
    padding: '1.2rem',
    color: inDarkMode ? '#fff' : '#000',
    fontSize: '13px',
    lineHeight: '1.4',
    resize: 'none' as const,
    outline: 'none',
    overflow: 'hidden' as const,
    transition: 'border-color 0.2s ease, height 0.3s ease',
    userSelect: 'text' as const,
    WebkitUserSelect: 'text' as const
  });

  const handleTextareaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.borderColor = inDarkMode ? '#939393' : '#888888';
  };

  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.borderColor = inDarkMode ? '#454545' : '#ddd';
  };

  const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = target.scrollHeight + 'px';
  };

  const handleSaveReflections = async () => {
    if (!reflections.trim()) {
      return;
    }

    if (reflections.trim() === originalReflections) {
      setShowReflectionsInput(false);
      return;
    }

    if (!summaryId) {
      console.error('No summary ID available');
      return;
    }

    trackEvent("/room/summary/save_reflections");
    setIsSavingReflections(true);
    try {
      const updatedSummary = { ...summary, reflections: reflections.trim() } as any;
      await updateJournalEntry(summaryId, updatedSummary);
      setSummary(updatedSummary);
      setOriginalReflections(reflections.trim());
      setShowReflectionsInput(false);

      setShowSavedToast(true);
      setTimeout(() => setShowSavedToast(false), 4000);
    } catch (error) {
      console.error('Failed to save reflections:', error);
    } finally {
      setIsSavingReflections(false);
    }
  };

  const handleSaveManualReflections = async () => {
    if (!manualReflections.trim()) return;

    trackEvent("/room/summary/save_manual_reflections");
    setIsSavingManualReflections(true);
    try {
      const roomName = window.location.pathname.split('/')[2];

      // Create manual summary with just reflections and card info
      const manualSummary = {
        cards: summaryData.cards.map(card => ({
          name: card.reversed ? `${card.cardName} Reversed` : card.cardName,
          position: card.slot === 'blank' ? null : (typeof card.slot === 'object' ? card.slot.name : card.slot),
          interpretation: '',
          imagePath: card.imagePath,
          reversed: card.reversed
        })),
        reflections: manualReflections.trim(),
        query: null,
        overall: '',
        question: '',
        gist: '',
        tarotTip: '',
        hint: ''
      };

      const response = await getRoomSummary(roomName, summaryData, manualSummary);
      setSummary(response.summary);
      setSummaryDate(response.createdAt ? new Date(response.createdAt) : new Date());
      setHasGeneratedSummary(true);
      setSummaryId(response.id);
      setSummaryUserId(user?.uuid || null);
      setOriginalSummaryData(summaryData);

      setShowManualReflections(false);
      setManualReflections('');
    } catch (error) {
      console.error('Failed to save manual reflections:', error);
    } finally {
      setIsSavingManualReflections(false);
    }
  };


  // Check if current summary is manual-only (missing AI-generated content)
  const isManualOnly = summary && (
    !summary.overall ||
    !summary.gist ||
    summary.cards.every(card => !card.interpretation)
  );

  return (
    <StyledSummaryOverlay
      Theme={inDarkMode ? "dark" : "light"}
      onClick={handleOverlayClick}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        height: isOnMobile ? '-webkit-fill-available' : '100%'
      }}
    >
      {showUpdateToast && (
        <div
          style={{
            position: 'fixed',
            top: isOnMobile
              ? 'calc(120px - 60px + env(safe-area-inset-bottom))'
              : 'calc(120px - 26px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: inDarkMode ? '#000' : '#fff',
            color: inDarkMode ? '#fff' : '#000',
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            opacity: 0,
            animation: `${fadeIn} 0.3s ease forwards .3s, ${fadeOut} 0.3s ease forwards 2.2s`,
            border: `0px solid ${inDarkMode ? '#444' : '#ddd'}`,
            zIndex: 1000,
          }}
        >
          Room updated ✓
        </div>
      )}
      {showSavedToast && (
        <div
          style={{
            position: 'fixed',
            top: isOnMobile
              ? 'calc(120px - 60px + env(safe-area-inset-bottom))'
              : 'calc(120px - 26px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: inDarkMode ? '#000' : '#fff',
            color: inDarkMode ? '#fff' : '#000',
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            opacity: 0,
            animation: `${fadeIn} 0.3s ease forwards .3s, ${fadeOut} 0.3s ease forwards 2.2s`,
            border: `0px solid ${inDarkMode ? '#444' : '#ddd'}`,
            zIndex: 1000,
          }}
        >
          Summary updated ✓
        </div>
      )}
      <div
        className="summary-container"
        onClick={e => {
          e.stopPropagation();
        }}
        style={{
          paddingTop: 0,
          paddingBottom: 0,
          overflowX: 'hidden',
          minHeight: isOnMobile ? `calc(${vh(100)} - 140px - env(safe-area-inset-bottom))` : `calc(${vh(100)} - 140px)`,
          maxHeight: isOnMobile ? `calc(${vh(100)} - 140px - env(safe-area-inset-bottom))` : `calc(${vh(100)} - 140px)`,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <div
          className="sticky-header"
          style={{
            position: 'sticky',
            top: '0',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '16px 10px',
            zIndex: 4,
            width: 'calc(100% + 64px)',
            marginLeft: '-32px',
            marginRight: '-32px',
            boxSizing: 'border-box',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px'
          }}>

          {/* X button */}
          <img
            className="x-button"
            onClick={onClose}
            src="/images/exit.svg"
            style={{
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'opacity 0.2s ease',
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '1'}
            onMouseOut={e => e.currentTarget.style.opacity = '0.6'}
          />
        </div>

        <div
          className="summary-content"
          style={{
            padding: '28px 16px',
            paddingBottom: '20px',
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'min-content',
            opacity: 0,
            animation: `${fadeIn} 0.5s ease-in-out forwards`,
            animationDelay: '0.1s'
          }}
        >

          {/* Card preview section */}
          <div className="card-previews" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            {summaryData.cards.map((card, index) => (
              <div key={index} className="card-preview-item" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                width: '80px'
              }}>
                <div style={{
                  transform: `rotate(${card.reversed && reversesOn ? '180deg' : '0deg'})`,
                  width: '100%'
                }}>
                  <div style={{
                    aspectRatio: `${deck.cardSize[0]}/${deck.cardSize[1]}`,
                    position: 'relative',
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 1
                    }}>
                      <CardLoadingSvg />
                    </div>
                    <img
                      src={card.imagePath}
                      alt={card.cardName}
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        borderRadius: '6px',
                        opacity: 0,
                        position: 'relative',
                        zIndex: 2,
                        objectFit: 'cover'
                      }}
                      onLoad={(e) => {
                        (e.target as HTMLImageElement).style.opacity = '1';
                        const loadingContainer = (e.target as HTMLImageElement).parentElement?.querySelector('div');
                        if (loadingContainer) {
                          loadingContainer.style.display = 'none';
                        }
                      }}
                    />
                  </div>
                </div>
                <div style={{
                  fontSize: '11px',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  color: inDarkMode ? '#fff' : '#000'
                }}>
                  {card.cardName}
                  {card.reversed && ' (R)'}
                  {typeof card.slot === 'object' && card.slot?.name && (
                    <div style={{
                      fontSize: '10px',
                      marginTop: '2px',
                      textTransform: 'none',
                      opacity: 0.5,
                      color: inDarkMode ? '#fff' : '#000'
                    }}>
                      {card.slot.name}
                    </div>
                  )}
                  {/* Keywords section */}
                  <div style={{
                    fontSize: '9px',
                    textAlign: 'center',
                    fontWeight: '300',
                    lineHeight: '.9rem',
                    marginTop: '7px',
                    textTransform: 'lowercase',
                    opacity: 0.9,
                    color: inDarkMode ? '#fff' : '#000',
                    letterSpacing: '.01em',
                  }}>
                    {card.keywords?.join(' • ')}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Manual reflections section - before generated summary */}
          {(!summary || dataChanged) && !isLoading && !isFetchingSummary && (
            <div style={{
              marginTop: '10px',
              marginBottom: summary ? '60px' : '160px'
            }}>
              <textarea
                value={manualReflections}
                onChange={(e) => {
                  setManualReflections(e.target.value);
                  if (!showManualReflections) setShowManualReflections(true);
                }}
                placeholder="My reflections..."
                style={getTextareaStyle('80px')}
                onFocus={handleTextareaFocus}
                onBlur={handleTextareaBlur}
                onInput={handleTextareaInput}
                onKeyDown={(e) => e.stopPropagation()}
                maxLength={500}
              />
              {showManualReflections && manualReflections.trim().length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: '6px',
                  justifyContent: 'right',
                  marginTop: '4px',
                  height: '30px',
                  alignItems: 'center'
                }}>
                  <button
                    onClick={() => {
                      setManualReflections('');
                      setShowManualReflections(false);
                    }}
                    style={{
                      padding: '7px',
                      borderRadius: '100px',
                      border: inDarkMode ? '1px solid #fff' : '1px solid #000',
                      backgroundColor: inDarkMode ? '#000' : '#fff',
                      color: inDarkMode ? '#fff' : '#000000',
                      fontSize: '12px',
                      cursor: 'pointer',
                      width: '84px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = inDarkMode ? '#333' : '#ebebeb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = inDarkMode ? '#000' : '#fff';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveManualReflections}
                    disabled={isSavingManualReflections}
                    style={{
                      padding: '7px',
                      borderRadius: '100px',
                      border: inDarkMode ? '1px solid #fff' : '1px solid #000',
                      backgroundColor: inDarkMode ? '#000' : '#fff',
                      color: inDarkMode ? '#fff' : '#000000',
                      fontSize: '12px',
                      cursor: 'pointer',
                      width: '84px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s ease',
                      ...(isSavingManualReflections && {
                        opacity: 0.7
                      })
                    }}
                    onMouseEnter={(e) => {
                      if (!isSavingManualReflections) {
                        e.currentTarget.style.backgroundColor = inDarkMode ? '#333' : '#ebebeb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSavingManualReflections) {
                        e.currentTarget.style.backgroundColor = inDarkMode ? '#000' : '#fff';
                      }
                    }}
                  >
                    {isSavingManualReflections ? (
                      <SimpleLoadingIndicator
                        style={{
                          width: '12px',
                          height: '12px',
                          position: 'static',
                          opacity: 0.7,
                          margin: '1px'
                        }}
                      />
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {summary && (
            <>
              {/* Date with horizontal line */}
              <div
                className="summary-date-divider"
                style={{
                  position: 'relative',
                  width: '96%',
                  margin: '20px auto',
                  textAlign: 'center'
                }}
              >
                {/* Horizontal line */}
                <div style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: inDarkMode ? 'var(--colors-gray600)' : 'var(--colors-gray400)',
                }} />

                {/* Floating date label */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: inDarkMode ? 'var(--colors-darkwash)' : 'var(--colors-wash)',
                  padding: '0 12px',
                  fontSize: '10px',
                  color: inDarkMode ? '#b1b1b1' : '#5b5b5b',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                }}>
                  {summaryDate && summaryDate.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </div>

              <StyledAnalysis Theme={inDarkMode ? "dark" : "light"}>

                {summary.query && (
                  <div className="query">
                    <p>{summary.query}</p>
                  </div>
                )}

                <div className="interpretation">
                  {summary.cards
                    .filter(card => dataChanged || (card.interpretation && card.interpretation.trim().length > 0))
                    .map((card, index) => {
                      const isReversed = card.name.includes('Reversed');
                      return (
                        <div key={index} className="card-section">
                          <img
                            src={card.imagePath}
                            alt={card.name}
                            style={{ transform: isReversed ? 'rotate(180deg)' : undefined }}
                          />
                          <div className="card-content">
                            <h1>{card.name} {card.position && `(${card.position})`}</h1>
                            {card.interpretation && card.interpretation.trim().length > 0 && (
                              <p>{card.interpretation}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>

                {summary.overall && (
                  <>
                    <h2>Overall</h2>
                    <div className="overall">
                      <p>{summary.overall}</p>
                    </div>
                  </>
                )}

                {summary.gist && (
                  <div className="takeaway">
                    <h2>Takeaway</h2>
                    <p>{summary.gist}</p>
                  </div>
                )}

                {summary.question && (
                  <div className="question">
                    <h2>Ask Yourself</h2>
                    <p>{summary.question}</p>
                  </div>
                )}

                {summary.tarotTip && (
                  <div className="tip">
                    <h2>Tarot Tip</h2>
                    <p>{summary.tarotTip}</p>
                  </div>
                )}

                {summary.hint && (
                  <div className="hint">
                    <h2>Hint</h2>
                    <p>{summary.hint}</p>
                  </div>
                )}

                {/* Reflections section - only show for summary creator */}
                {user?.uuid === summaryUserId && (
                  <div style={{ marginTop: '40px', marginBottom: '20px' }}>
                    <h2 style={{
                      fontSize: '27px',
                      fontFamily: 'KeplerLightCondensed',
                      marginTop: '2rem',
                      marginBottom: '0.5rem',
                      fontWeight: 100
                    }}>
                      My Reflections
                    </h2>
                    {showReflectionsInput || !originalReflections ? (
                      <>
                        <textarea
                          value={reflections}
                          onChange={(e) => setReflections(e.target.value)}
                          placeholder="What's jumping out to you?"
                          style={getTextareaStyle('80px')}
                          onFocus={handleTextareaFocus}
                          onBlur={handleTextareaBlur}
                          onInput={handleTextareaInput}
                          onKeyDown={(e) => e.stopPropagation()}
                          maxLength={500}
                        />
                        <div style={{
                          display: 'flex',
                          gap: '6px',
                          justifyContent: 'right',
                          marginTop: '4px',
                          height: '30px',
                          alignItems: 'center'
                        }}>
                          {reflections.trim().length > 0 && (
                            <>
                              <button
                                onClick={() => {
                                  setReflections(summary?.reflections || '');
                                  setShowReflectionsInput(false);
                                }}
                                style={{
                                  padding: '7px',
                                  borderRadius: '100px',
                                  border: inDarkMode ? '1px solid #fff' : '1px solid #000',
                                  backgroundColor: inDarkMode ? '#000' : '#fff',
                                  color: inDarkMode ? '#fff' : '#000000',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  width: '84px',
                                  transition: 'background-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = inDarkMode ? '#333' : '#ebebeb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = inDarkMode ? '#000' : '#fff';
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveReflections}
                                disabled={isSavingReflections}
                                style={{
                                  padding: '7px',
                                  borderRadius: '100px',
                                  border: inDarkMode ? '1px solid #fff' : '1px solid #000',
                                  backgroundColor: inDarkMode ? '#000' : '#fff',
                                  color: inDarkMode ? '#fff' : '#000000',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  width: '84px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s ease',
                                  ...(isSavingReflections && {
                                    opacity: 0.7
                                  })
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSavingReflections) {
                                    e.currentTarget.style.backgroundColor = inDarkMode ? '#333' : '#ebebeb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSavingReflections) {
                                    e.currentTarget.style.backgroundColor = inDarkMode ? '#000' : '#fff';
                                  }
                                }}
                              >
                                {isSavingReflections ? (
                                  <SimpleLoadingIndicator
                                    style={{
                                      width: '12px',
                                      height: '12px',
                                      position: 'static',
                                      opacity: 0.7,
                                      margin: '1px'
                                    }}
                                  />
                                ) : (
                                  'Save'
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{
                          padding: '1.2rem',
                          backgroundColor: inDarkMode ? 'var(--colors-gray800)' : 'rgba(129, 129, 129, 0.1)',
                          border: `1px solid ${inDarkMode ? 'var(--colors-gray600)' : 'var(--colors-gray500)'}`,
                          borderRadius: '12px',
                          fontSize: '13px',
                          lineHeight: '1.4',
                          color: inDarkMode ? '#fff' : '#000',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {originalReflections}
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'right',
                          marginTop: '12px'
                        }}>
                          <button
                            onClick={() => {
                              setShowReflectionsInput(true);
                              // Auto-focus the textarea after it renders
                              setTimeout(() => {
                                const textarea = document.querySelector('textarea[placeholder="What\'s jumping out to you?"]') as HTMLTextAreaElement;
                                if (textarea) {
                                  textarea.focus();
                                  // Position cursor at end of text
                                  textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                  // Auto-expand to fit content
                                  textarea.style.height = 'auto';
                                  textarea.style.height = textarea.scrollHeight + 'px';
                                }
                              }, 0);
                            }}
                            style={{
                              padding: '7px',
                              border: inDarkMode ? '1px solid #fff' : '1px solid #000',
                              borderRadius: '100px',
                              backgroundColor: inDarkMode ? '#000' : '#fff',
                              color: inDarkMode ? '#fff' : '#000',
                              fontSize: '12px',
                              width: '84px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = inDarkMode ? '#333' : '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = inDarkMode ? '#000' : '#fff';
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </StyledAnalysis>
            </>
          )}


          {/* Loading indicator when fetching summary */}
          {isFetchingSummary && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 0',
              gap: '16px'
            }}>
              <SimpleLoadingIndicator
                style={{
                  width: '24px',
                  height: '24px',
                  position: 'static',
                  marginTop: '0',
                  opacity: .1
                }}
              />
            </div>
          )}

          {summary && user?.uuid === summaryUserId && (
            <div className="summary-footer-note" style={{
              alignItems: 'center',
              padding: '0 10px',
              fontSize: '11px',
              textAlign: 'center',
              color: inDarkMode ? '#b1b1b1' : '#767676',
              lineHeight: '1.4',
              marginTop: '10px',
              marginBottom: '30px'
            }}>
              ✓ Saved in your journal
            </div>)
          }

          {/* Copy button section removed */}
        </div>


        {/* Render footer if we haven't generated a summary yet, or if data has changed, and we're not fetching one */}
        {(!hasGeneratedSummary || isManualOnly || dataChanged) && !isFetchingSummary && (
          <StyledSummaryFooter
            Theme={inDarkMode ? "dark" : "light"}
            style={{
              opacity: 0,
              animation: `${fadeIn} 0.5s ease forwards`,
              animationDelay: '0.12s'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
              margin: '-2px auto 8px',
              transition: 'all 0.2s ease-in-out'
            }}>
              {showConfirm ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  opacity: 0,
                  animation: `${fadeIn} 0.5s ease forwards`,
                  animationDelay: '0.12s',
                  position: 'relative'
                }}>
                  <Button
                    Theme={inDarkMode ? "dark" : "light"}
                    className="arrow-button"
                    role="menuitem"
                    style={{
                      position: 'absolute',
                      left: isOnMobile ? '-96px' : '-122px',
                      top: isOnMobile ? '-1px' : '16px',
                      transform: isOnMobile ? 'none' : 'translateY(-50%)'
                    }}
                    transparent
                    onClick={() => setShowConfirm(false)}
                  >
                    &larr;
                  </Button>
                  <div style={{
                    textAlign: 'center',
                    fontSize: '12px',
                    opacity: 1,
                    maxWidth: '300px',
                    lineHeight: '1.4',
                    marginTop: '8px',
                    height: 'auto',
                    transition: 'all 0.2s ease-in-out'
                  }}>
                    Use current spread and notes?
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '2px',
                    alignItems: 'center',
                    gap: '12px',
                    height: 'auto',
                    transition: 'all 0.2s ease-in-out'
                  }}>
                    <ContinueButton
                      onClick={handleGetSummary}
                      Theme={inDarkMode ? "dark" : "light"}
                      style={{
                        padding: '10px 34px',
                        borderRadius: '100px',
                        border: '0px solid'
                      }}
                    >
                      Continue
                    </ContinueButton>
                  </div>
                </div>
              ) : (
                <div
                  key={showConfirm ? 'confirm' : 'interpret'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '14px',
                    marginTop: '9px',
                    opacity: 0,
                    animation: `${fadeIn} 0.5s ease forwards`,
                    animationDelay: '0.12s'
                  }}>
                  <StyledAnalysisButton
                    className="analysis-button"
                    onClick={() => {
                      if (dataChanged) {
                        trackEvent("/room/summary/interpret_again");
                      } else {
                        trackEvent("/room/summary/interpret");
                      }
                      handleGetSummary();
                    }}
                    disabled={isLoading}
                    Theme={inDarkMode ? "dark" : "light"}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease-in-out',
                      ...(isLoading && {
                        backgroundColor: 'transparent',
                        border: 'none'
                      })
                    }}
                  >
                    {isLoading ? (
                      <>
                        <SimpleLoadingIndicator
                          style={{
                            width: '16px',
                            height: '16px',
                            position: 'static',
                            marginTop: '0'
                          }}
                        />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      dataChanged ? 'Interpret again' : 'Help interpret'
                    )}
                  </StyledAnalysisButton>
                </div>
              )}
            </div>
          </StyledSummaryFooter>
        )}
      </div>
    </StyledSummaryOverlay>
  );
};
